import { Bookmark, Eye, Pencil, RotateCcw, Search, Trash2, X } from 'lucide-react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { caseService } from '../api/services/case.service'
import { PermissionGate } from '../components/auth/PermissionGate'
import { CaseStatusStateBadges } from '../components/cases/CaseStatusBadge'
import { AppShell } from '../components/layout/AppShell'
import { Button } from '../components/ui/button'
import { FieldLabel } from '../components/ui/field-label'
import { Input } from '../components/ui/input'
import { SelectPicker, type SelectOption } from '../components/ui/select-picker'
import { SoftDeleteConfirmDialog } from '../components/ui/soft-delete-confirm-dialog'
import { IconActionButton, TableIconActions } from '../components/ui/table-icon-actions'
import { ListPagination } from '../components/ui/list-pagination'
import { useAuth } from '../context/AuthContext'
import { DEFAULT_PAGE_SIZE, resolveListPage, type PaginationMeta } from '../lib/api-list'
import { canDeleteCase, canMutateCase } from '../lib/case-capabilities'
import type { AssistantDoctorLink } from '../types/assistant'
import {
  CASE_PHASE_FILTERS,
  CASE_PHASE_LABELS,
  CASE_STATE_LABELS,
  CASE_STATES,
  CASE_STATUS_LABELS,
  CASE_STATUSES,
  type Case,
  type CasePhaseFilter,
  type CaseState,
  type CaseStatus,
} from '../types/case'
import { USER_ROLES } from '../types/auth'

type StatusFilter = 'all' | CaseStatus
type PhaseFilter = CasePhaseFilter | 'all'
type StateFilter = 'all' | CaseState

const DEFAULT_PHASE: PhaseFilter = 'all'
const DEFAULT_STATUS: StatusFilter = 'all'
const DEFAULT_STATE: StateFilter = 'all'

const PHASE_OPTIONS: SelectOption[] = [
  { value: 'all', label: 'All phases' },
  ...CASE_PHASE_FILTERS.map((phase) => ({
    value: phase,
    label: CASE_PHASE_LABELS[phase],
  })),
]

const STATUS_OPTIONS: SelectOption[] = [
  { value: 'all', label: 'All statuses' },
  ...CASE_STATUSES.map((status) => ({
    value: status,
    label: CASE_STATUS_LABELS[status],
  })),
]

const STATE_OPTIONS: SelectOption[] = [
  { value: 'all', label: 'All states' },
  ...CASE_STATES.map((state) => ({
    value: state,
    label: CASE_STATE_LABELS[state],
  })),
]

function isArchived(caseItem: Case) {
  return caseItem.is_archived
}

export function CasesPage() {
  const { can, session } = useAuth()
  const linkedDoctors = (session?.assistant_links ?? []) as AssistantDoctorLink[]
  const isDoctor = session?.role === USER_ROLES.DOCTOR
  const [cases, setCases] = useState<Case[]>([])
  const [hasLoaded, setHasLoaded] = useState(false)
  const [phaseFilter, setPhaseFilter] = useState<PhaseFilter>('all')
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all')
  const [stateFilter, setStateFilter] = useState<StateFilter>('all')
  const [assistantFilter, setAssistantFilter] = useState('all')
  const [assistantOptions, setAssistantOptions] = useState<SelectOption[]>([
    { value: 'all', label: 'All assistants' },
  ])
  const [search, setSearch] = useState('')
  const [searchInput, setSearchInput] = useState('')
  const [bookmarkedOnly, setBookmarkedOnly] = useState(false)
  const [withTrashed, setWithTrashed] = useState(false)
  const [page, setPage] = useState(1)
  const [perPage, setPerPage] = useState<number>(DEFAULT_PAGE_SIZE)
  const [meta, setMeta] = useState<PaginationMeta | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<Case | null>(null)
  const [deleting, setDeleting] = useState(false)

  const canMutateRow = (caseItem: Case) => {
    if (session?.role !== USER_ROLES.ASSISTANT) {
      return true
    }

    return canMutateCase(session.role, caseItem, linkedDoctors, session.user.id)
  }

  const canDeleteRow = (caseItem: Case) => {
    if (!session?.role || !session.user.id) {
      return false
    }

    if (session.role !== USER_ROLES.ASSISTANT) {
      return canMutateRow(caseItem)
    }

    return canDeleteCase(session.role, caseItem, linkedDoctors, session.user.id)
  }

  useEffect(() => {
    if (!isDoctor) {
      return
    }

    void caseService.filters().then((response) => {
      const assistants = response.data.assistants ?? []
      setAssistantOptions([
        { value: 'all', label: 'All assistants' },
        ...assistants.map((assistant) => ({
          value: assistant.id,
          label: assistant.name,
        })),
      ])
    }).catch(() => {
      setAssistantOptions([{ value: 'all', label: 'All assistants' }])
    })
  }, [isDoctor])

  const loadCases = useCallback(async () => {
    try {
      const response = await caseService.list({
        phase: phaseFilter !== 'all' ? phaseFilter : undefined,
        status: statusFilter === 'all' ? undefined : statusFilter,
        state: stateFilter === 'all' ? undefined : stateFilter,
        uploaded_by:
          isDoctor && assistantFilter !== 'all' ? assistantFilter : undefined,
        search: search || undefined,
        // Service layer sends bookmarked=1 for Laravel boolean parity.
        bookmarked: bookmarkedOnly || undefined,
        only_trashed: withTrashed || undefined,
        page,
        per_page: perPage,
      })

      const { items, meta: nextMeta } = resolveListPage(response, page, perPage)
      setCases(items)
      setMeta(nextMeta)
    } catch {
      setCases([])
      setMeta(null)
    } finally {
      setHasLoaded(true)
    }
  }, [
    assistantFilter,
    bookmarkedOnly,
    isDoctor,
    page,
    perPage,
    phaseFilter,
    search,
    stateFilter,
    statusFilter,
    withTrashed,
  ])

  useEffect(() => {
    void loadCases()
  }, [loadCases])

  useEffect(() => {
    setPage(1)
  }, [phaseFilter, statusFilter, stateFilter, assistantFilter, search, bookmarkedOnly, withTrashed])

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) {
      return
    }

    setDeleting(true)

    try {
      await caseService.remove(deleteTarget.id)
      setDeleteTarget(null)
      await loadCases()
    } catch {
      // handled globally
    } finally {
      setDeleting(false)
    }
  }

  const handleRestore = async (caseItem: Case) => {
    try {
      await caseService.restore(caseItem.id)
      await loadCases()
    } catch {
      // handled globally
    }
  }

  const procedureDoctorLabel = (caseItem: Case) => {
    const procedure = caseItem.procedure?.name ?? '—'
    const doctor = caseItem.belongs_to_doctor?.name ?? '—'
    return `${procedure} / ${doctor}`
  }

  const activeFilterCount = useMemo(() => {
    let count = 0
    if (phaseFilter !== DEFAULT_PHASE) count += 1
    if (statusFilter !== DEFAULT_STATUS) count += 1
    if (stateFilter !== DEFAULT_STATE) count += 1
    if (isDoctor && assistantFilter !== 'all') count += 1
    if (search) count += 1
    if (bookmarkedOnly) count += 1
    if (withTrashed) count += 1
    return count
  }, [
    assistantFilter,
    bookmarkedOnly,
    isDoctor,
    phaseFilter,
    search,
    stateFilter,
    statusFilter,
    withTrashed,
  ])

  const clearFilters = () => {
    setPhaseFilter(DEFAULT_PHASE)
    setStatusFilter(DEFAULT_STATUS)
    setStateFilter(DEFAULT_STATE)
    setAssistantFilter('all')
    setSearch('')
    setSearchInput('')
    setBookmarkedOnly(false)
    setWithTrashed(false)
  }

  return (
    <AppShell>
      <div className="mx-auto max-w-6xl px-2 py-2">
        <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0 flex-1">
            <h1 className="font-display text-2xl font-bold">My Cases</h1>
            <p className="mt-1 text-sm text-ink-600">
              Browse cases by clinical phase (pre-op / post-op), then filter by publication status and workflow state.
            </p>
          </div>

          <div className="ml-auto flex flex-wrap items-center justify-end gap-3">
            <label className="inline-flex cursor-pointer items-center gap-2 text-sm text-ink-700">
              <input
                type="checkbox"
                className="h-4 w-4 accent-green-600"
                checked={bookmarkedOnly}
                onChange={(event) => setBookmarkedOnly(event.target.checked)}
              />
              Bookmarked only
            </label>
            <label className="inline-flex cursor-pointer items-center gap-2 text-sm text-ink-700">
              <input
                type="checkbox"
                className="h-4 w-4 accent-green-600"
                checked={withTrashed}
                onChange={(event) => setWithTrashed(event.target.checked)}
              />
              Show Deleted
            </label>
            <PermissionGate permission="cases-create">
              <Button asChild>
                <Link to="/cases/new">Upload a New Case</Link>
              </Button>
            </PermissionGate>
          </div>
        </div>

        <section className="mb-6 rounded-xl border border-parch-100 bg-white p-4 shadow-card sm:p-5">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <h2 className="text-sm font-semibold text-ink-900">Filters</h2>
              {activeFilterCount > 0 && (
                <span className="inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-green-100 px-1.5 text-xs font-semibold text-green-700">
                  {activeFilterCount}
                </span>
              )}
            </div>
          </div>

          <div className="mt-4 flex flex-wrap items-stretch gap-2">
            <div className="relative min-w-[12rem] flex-1">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-400" />
              <Input
                className="pl-9"
                placeholder="Search reference, note, outcome…"
                value={searchInput}
                onChange={(event) => setSearchInput(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    setSearch(searchInput.trim())
                  }
                }}
              />
            </div>
            <Button onClick={() => setSearch(searchInput.trim())}>Search</Button>
            {activeFilterCount > 0 && (
              <Button variant="secondary" onClick={clearFilters}>
                <X className="h-4 w-4" />
                Clear
              </Button>
            )}
          </div>

          <div className="mt-5 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <div>
              <FieldLabel>Phase</FieldLabel>
              <SelectPicker
                options={PHASE_OPTIONS}
                value={phaseFilter}
                onChange={(value) => setPhaseFilter(value as PhaseFilter)}
                placeholder="All phases"
              />
            </div>
            <div>
              <FieldLabel>Status</FieldLabel>
              <SelectPicker
                options={STATUS_OPTIONS}
                value={statusFilter}
                onChange={(value) => setStatusFilter(value as StatusFilter)}
                placeholder="All statuses"
              />
            </div>
            <div>
              <FieldLabel>State</FieldLabel>
              <SelectPicker
                options={STATE_OPTIONS}
                value={stateFilter}
                onChange={(value) => setStateFilter(value as StateFilter)}
                placeholder="All states"
              />
            </div>
            {isDoctor && (
              <div>
                <FieldLabel>Uploaded By</FieldLabel>
                <SelectPicker
                  options={assistantOptions}
                  value={assistantFilter}
                  onChange={setAssistantFilter}
                  placeholder="All assistants"
                />
              </div>
            )}
          </div>
        </section>

        <section className="overflow-hidden rounded-lg border border-parch-100 bg-white shadow-card">
          <div className="overflow-x-auto">
            <table className="min-w-full text-left text-sm">
              <thead className="border-b border-parch-100 bg-parch-50 text-ink-600">
                <tr>
                  <th className="px-4 py-3 font-medium">Reference</th>
                  <th className="px-4 py-3 font-medium">Status / state</th>
                  <th className="px-4 py-3 font-medium">Procedure / consultant</th>
                  <th className="px-4 py-3 font-medium">Procedure date</th>
                  <th className="px-4 py-3 font-medium">Visibility</th>
                  <th className="px-4 py-3 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {hasLoaded && cases.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-4 py-6 text-sm text-ink-500">
                      No cases found.
                    </td>
                  </tr>
                ) : (
                  cases.map((caseItem) => (
                    <tr key={caseItem.id} className="border-b border-parch-50 last:border-0">
                      <td className="px-4 py-3 font-medium">
                        <span className="inline-flex items-center gap-1.5">
                          {caseItem.is_bookmarked ? (
                            <Bookmark
                              className="h-3.5 w-3.5 shrink-0 fill-green-700 text-green-700"
                              aria-label="Bookmarked"
                            />
                          ) : null}
                          <span>{caseItem.reference}</span>
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        {caseItem.deleted_at ? (
                          <span className="text-danger">Deleted</span>
                        ) : (
                          <CaseStatusStateBadges caseItem={caseItem} />
                        )}
                      </td>
                      <td className="px-4 py-3">{procedureDoctorLabel(caseItem)}</td>
                      <td className="px-4 py-3">{caseItem.procedure_date ?? '—'}</td>
                      <td className="px-4 py-3">{caseItem.is_global ? 'Public' : 'Private'}</td>
                      <td className="px-4 py-3">
                        <TableIconActions>
                          <IconActionButton
                            icon={Eye}
                            label="View"
                            to={`/cases/${caseItem.id}`}
                          />
                          {can('cases-update') &&
                            canMutateRow(caseItem) &&
                            !caseItem.deleted_at &&
                            !isArchived(caseItem) && (
                              <IconActionButton
                                icon={Pencil}
                                label="Edit"
                                to={`/cases/${caseItem.id}/edit`}
                              />
                            )}
                          {can('cases-delete') && canDeleteRow(caseItem) && !caseItem.deleted_at && (
                            <IconActionButton
                              icon={Trash2}
                              label="Delete"
                              variant="destructive"
                              onClick={() => setDeleteTarget(caseItem)}
                            />
                          )}
                          {can('cases-restore') && canMutateRow(caseItem) && caseItem.deleted_at && (
                            <IconActionButton
                              icon={RotateCcw}
                              label="Restore"
                              onClick={() => void handleRestore(caseItem)}
                            />
                          )}
                        </TableIconActions>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </section>

        <ListPagination
          meta={meta}
          perPage={perPage}
          onPageChange={setPage}
          onPerPageChange={(next) => {
            setPerPage(next)
            setPage(1)
          }}
        />

        <SoftDeleteConfirmDialog
          open={deleteTarget !== null}
          entityLabel="case"
          itemName={deleteTarget?.reference ?? ''}
          onCancel={() => setDeleteTarget(null)}
          onConfirm={() => void handleDeleteConfirm()}
          confirming={deleting}
        />
      </div>
    </AppShell>
  )
}
